import { useCallback, useEffect, useState } from 'react';
import Lottie from 'lottie-react';
import { useLocation } from 'react-router-dom';
import _ from 'lodash';
import appCompoLoading from '@/assets/animation/loading.json';
import { useGetPaymentDetails } from '../api/getPaymentDetails';
import { useMakePaymentMutation } from '../api/makePayment';
import { PetskedTitle } from '@/components/Heading/PetskedTitle';
import './RequestPayment.scss';
import { AppointmentData } from '@/features/availability/types';
import { AppointmentInfoPanel } from '@/features/availability/components/AppointmentScheduleDetails';
import { CreditCardNonRegistration } from '@/features/availability/components/CreditCardNonRegistration';
import { NonRegistrationConfirmation } from '@/features/availability/components/NonRegistrationConfirmation';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { AppointmentFormConfirmation } from '@/features/availability/components/AppointmentConfirmation';
import PAYMENT_TYPES from '@/config/constants';
import Utils from '@/utils/util';
import { useTranslation } from 'react-i18next';

export default function PaymentRequestStripe() {
  const search = useLocation().search;
  const token = new URLSearchParams(search).get('token') || '';
  const paymentDetailsQuery = useGetPaymentDetails({ token: token });
  const [paymentData, setPaymentData] = useState<any>();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    setPaymentData(paymentDetailsQuery.data);
  }, [paymentDetailsQuery.data]);

  // useEffect(() => {
  //   if (paymentData) {
  //     const language = paymentData?.business?.country === 'New Zealand' ? 'en-NZ' : 'en-US';
  //     i18n.changeLanguage(language).then(() => {
  //       console.log(`Language changed to: ${i18n.language}`);
  //     });
  //   }
  // }, [paymentData]);

  if (paymentDetailsQuery.isLoading || !paymentData) {
    return (
      <section className={`petsked-container-bg request-payment`}>
        <div className="preloader-schedule-new">
          <Lottie className="loader" animationData={appCompoLoading} />
        </div>
      </section>
    );
  }

  const stripeKey =
    paymentData.paymentMode === 'sandbox'
      ? process.env.REACT_APP_STRIPE_KEY_SANDBOX
      : process.env.REACT_APP_STRIPE_KEY;
  const stripePromise = loadStripe(stripeKey as string);

  return (
    <Elements stripe={stripePromise}>
      <PaymentRequest paymentData={paymentData} token={token} />
    </Elements>
  );
}

export const PaymentRequest = ({ paymentData, token }: { paymentData: any; token: string }) => {
  const stripe = useStripe();
  const makePaymentMutation = useMakePaymentMutation();
  const [appointmentData, setAppointmentData] = useState<AppointmentData>();
  const [currentPage, setCurrentPage] = useState<
    'capture-payment' | 'pre-confirmation' | 'successfull'
  >();

  useEffect(() => {
    const data = paymentData;
    if (data && data.appointment) {
      const apptData: AppointmentData = {
        amount: data.amount,
        id: data.appointment.id,
        service: data.appointment.service,
        provider: data.appointment.businessUser,
        tz: data.appointment.userTz,
        date: data.appointment.startTime,
        duration: data.appointment.duration,
        business: data.business,
        slot: {
          label: '',
          id: 0,
          start: data.appointment.startTime,
          end: data.appointment.endTime,
        },
        userData: {
          name: data.appointment.petParent.name,
          phone: data.appointment.petParent.phone,
          email: data.appointment.petParent.email,
          altEmails: [],
          receiveUpdates: false,
        },
        petProfiles: data.appointment.pet
          ? [data.appointment.pet]
          : data.appointment.pets
          ? data.appointment.pets
          : [],
        selectedAddons: data.appointment.addonServices,
        room: data.appointment?.room ? data.appointment?.room : undefined,
        accomodationType: data.appointment?.accomodationType,
        startDate: data.appointment.startTime,
        endDate: data.appointment.endTime,
        intakeFormLink: data.appointment.intakeFormLink,
      };
      if (PAYMENT_TYPES.SUCCESS.indexOf(data.status) >= 0) {
        setCurrentPage('successfull');
      } else {
        setCurrentPage('capture-payment');
      }
      setAppointmentData(apptData);
    }
  }, [paymentData]);

  useEffect(() => {}, [paymentData]);
  //console.log('testsssssssss',paymentData.appointment.businessLocationId);

  const makePayment = useCallback(
    async (data: AppointmentData) => {
      if (data && token) {
        try {
          const requestData = {
            data: {
              token,
              cardToken: data?.paymentCard?.id || data?.paymentCard?.squareCardId,
              paymentIntentId: '',
            },
          };
          let bookingResponse: any = await makePaymentMutation.mutateAsync(requestData);
          if (
            bookingResponse.paymentIntent &&
            bookingResponse.paymentIntent.next_action &&
            stripe
          ) {
            const { error: confirmError, paymentIntent }: any = await stripe.confirmCardPayment(
              bookingResponse.paymentIntent.client_secret,
              {
                payment_method: bookingResponse.paymentIntent.payment_method,
              }
            );
            if (confirmError) {
              console.error(confirmError);
              requestData.data.paymentIntentId = bookingResponse.paymentIntent.id;
              bookingResponse = await makePaymentMutation.mutateAsync(requestData);
              setCurrentPage('successfull');
            } else {
              requestData.data.paymentIntentId = paymentIntent.id;
              bookingResponse = await makePaymentMutation.mutateAsync(requestData);
              setCurrentPage('successfull');
            }
          } else {
            setCurrentPage('successfull');
          }
        } catch (e: any) {
          console.log('e', e);
        }
      }
    },
    [token, paymentData]
  );
  const onAppointmentDataChange = (appointmentData: AppointmentData) => {
    setAppointmentData({ ...appointmentData });
  };
  const cardSelectionSuccess = (data: AppointmentData) => {
    onAppointmentDataChange(data);
    setCurrentPage('pre-confirmation');
  };
  return (
    <section
      className={`petsked-container-bg request-payment ${currentPage} ${
        currentPage == 'successfull' ? 'scroll' : 'no-scroll'
      }`}
    >
      <>
        {currentPage != 'successfull' ? (
          <>
            <PetskedTitle
              title="Payment request for"
              highlightTitle={`${appointmentData?.room ? 'booking' : 'appointment'} #${
                appointmentData?.id
              }`}
              size="lg"
              alignText="left"
              description=""
            />
            <div className="details">
              <AppointmentInfoPanel
                business={appointmentData?.business}
                data={appointmentData}
                showExtraInfo={currentPage === 'pre-confirmation' ? false : true}
                isPaymentViaLink={true}
              />
              <div className="right-pages-schedule" id="righ-side-scroller">
                {currentPage == 'capture-payment' && appointmentData && (
                  <CreditCardNonRegistration
                    appointmentData={appointmentData}
                    onSuccess={cardSelectionSuccess}
                    isLoading={makePaymentMutation.isLoading}
                    hideBackNav={true}
                    paymentMode={paymentData.paymentMode}
                    locationId={paymentData.appointment.businessLocationId}
                  />
                )}
                {currentPage == 'pre-confirmation' && appointmentData && (
                  <NonRegistrationConfirmation
                    appointmentData={appointmentData}
                    business={appointmentData.business}
                    onComplete={makePayment}
                    onBack={() => setCurrentPage('capture-payment')}
                    isLoading={makePaymentMutation.isLoading}
                    isPaymentViaLink={true}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <div>
            <AppointmentFormConfirmation
              isPaymentConfirmation={true}
              business={appointmentData?.business}
              appointmentData={appointmentData}
            />
          </div>
        )}
      </>
    </section>
  );
};
